import { map } from 'lodash'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useOverlay } from '~/components/Overlay'
import { useListInAppNotificationsQuery } from '~/data/api/client/workflows'
import { usePolling } from '~/data/api/hooks/polling'
import useFirstRender from '~/hooks/useFirstRender'
import {
  clearOverlayNotifications,
  removeOverlayNotifications,
  setOverlayOpen,
  syncOverlayWorkflowNotifications,
  useNotificationOverlayOpen,
  useOverlayWorkflowNotifications
} from '~/store/slices/notifications'

import NotificationsOverlay from './NotificationsOverlay'

const WORKFLOWS_POLL_SECONDS = 15

/**
 * Polls for unread Critical Workflows in-app notifications and shows them in a
 * single overlay, newest first. Mount it once at the layout level so it works
 * with whichever notifications bell renders. The poll runs even while the
 * window is unfocused, so the overlay is current when the user returns and the
 * desktop shell can raise native notifications from the same cache entry. Rows
 * the user closed are remembered for the session and do not reopen the overlay.
 */
export default function CriticalNotificationsOverlay() {
  const dispatch = useDispatch()
  const isOverlayOpen = useNotificationOverlayOpen()
  const rows = useOverlayWorkflowNotifications()
  const overlay = useOverlay()
  const [seenNotifications, setSeenNotifications] = useState<string[]>([])

  const { data: workflowResponse } = usePolling(
    useListInAppNotificationsQuery,
    { priority: 'Critical', read: false, page: 0, count: 100 },
    { intervalSeconds: WORKFLOWS_POLL_SECONDS, ignoreFocus: true }
  )

  useFirstRender(() => {
    dispatch(clearOverlayNotifications())
  })

  useEffect(() => {
    if (!workflowResponse) return
    const unreadRows = workflowResponse.data.filter(({ id, read }) => !read && !seenNotifications.includes(id))
    dispatch(syncOverlayWorkflowNotifications(unreadRows))
  }, [dispatch, workflowResponse, seenNotifications])

  useEffect(() => {
    if (isOverlayOpen) overlay.openOverlay()
    else overlay.closeOverlay()
  }, [isOverlayOpen, overlay])

  return (
    <NotificationsOverlay
      rows={rows}
      onRowsAcknowledged={(ids) => setSeenNotifications((prev) => [...prev, ...ids])}
      onClose={() => {
        const shownIds = map(rows, 'id')
        setSeenNotifications((prev) => [...prev, ...shownIds])
        dispatch(removeOverlayNotifications(shownIds))
        dispatch(setOverlayOpen(false))
      }}
      {...overlay}
    />
  )
}
